// QuizResponseService.js
// Servicio para manejar el guardado de respuestas de cuestionarios

import axios from 'axios';
import { ENDPOINTS as LMS_ENDPOINTS } from './Endpoints';

/**
 * Guarda un intento completo de cuestionario con todas sus respuestas
 * @param {Object} params - Parámetros del intento
 * @param {number} params.idCuestionario - ID del cuestionario
 * @param {number} params.idEstudiante - ID del estudiante
 * @param {Array} params.preguntas - Array de preguntas del cuestionario
 * @param {Object} params.respuestasUsuario - Objeto con las respuestas del usuario
 * @param {Object} params.resultado - Objeto con los resultados calculados
 * @returns {Promise<Object>} - Resultado del guardado
 */
export const saveQuizAttempt = async ({
    idCuestionario,
    idEstudiante,
    preguntas,
    respuestasUsuario,
    resultado
}) => {
    try {
        console.log('[QuizResponseService] Iniciando guardado de intento de cuestionario...');
        console.log('Parámetros:', { idCuestionario, idEstudiante, totalPreguntas: preguntas.length });

        // Paso 1: Crear el intento de cuestionario
        const calificacion = resultado.total > 0
            ? (resultado.correctas / resultado.total) * 100
            : 0;

        const aprobado = calificacion >= 60; // Asumiendo 70% como mínimo aprobatorio

        const intentoData = {
            IdIntento: 0, // Nuevo intento
            IdCuestionario: idCuestionario,
            IdEstudiante: idEstudiante,
            FechaIntento: new Date().toISOString(),
            Resultado: aprobado,
            Calificacion: calificacion,
            Activo: true
        };

        console.log('[QuizResponseService] Creando intento:', intentoData);
        const resIntento = await axios.post(LMS_ENDPOINTS.cuestionariosIntentos, intentoData);

        // El backend puede retornar el ID directamente o en data
        const idIntento = typeof resIntento.data === 'number'
            ? resIntento.data
            : resIntento.data?.idIntento || resIntento.data?.IdIntento;

        if (!idIntento) {
            throw new Error('No se pudo obtener el ID del intento creado');
        }

        console.log('[QuizResponseService] Intento creado con ID:', idIntento);

        // Paso 2: Guardar cada respuesta individual
        const respuestasGuardadas = [];
        const errores = [];

        for (const pregunta of preguntas) {
            try {
                const idPregunta = pregunta.idPregunta;

                // Determinar el tipo de pregunta y obtener la respuesta
                const tipoRespuesta = determinarTipoRespuesta(pregunta, respuestasUsuario, idPregunta);

                if (!tipoRespuesta.contestada) {
                    console.log(`[QuizResponseService] Pregunta ${idPregunta} no contestada, omitiendo...`);
                    continue;
                }

                // Guardar la respuesta
                const resultadoRespuesta = await saveIndividualResponse({
                    idIntento,
                    pregunta,
                    tipoRespuesta,
                    idPregunta
                });

                respuestasGuardadas.push(resultadoRespuesta);
                console.log(`[QuizResponseService] Respuesta guardada para pregunta ${idPregunta}`);

            } catch (error) {
                console.error(`[QuizResponseService] Error guardando respuesta para pregunta ${pregunta.idPregunta}:`, error);
                errores.push({
                    idPregunta: pregunta.idPregunta,
                    error: error.message
                });
            }
        }

        console.log('[QuizResponseService] Guardado completado');
        console.log(`Total respuestas guardadas: ${respuestasGuardadas.length}`);
        console.log(`Total errores: ${errores.length}`);

        return {
            success: true,
            idIntento,
            respuestasGuardadas: respuestasGuardadas.length,
            errores: errores.length > 0 ? errores : null,
            calificacion,
            aprobado
        };

    } catch (error) {
        console.error('[QuizResponseService] Error general al guardar intento:', error);
        throw error;
    }
};

/**
 * Determina el tipo de respuesta y si fue contestada
 */
const determinarTipoRespuesta = (pregunta, respuestasUsuario, idPregunta) => {
    // Verificar si es pregunta con espacios en blanco (texto libre)
    const descripcion = pregunta.descripcionPregunta || pregunta.DescripcionPregunta || pregunta.descripcion || '';
    const tieneEspaciosBlanco = descripcion.includes('{}');

    if (tieneEspaciosBlanco) {
        // Buscar respuestas de espacios en blanco
        const respuestasBlanco = [];
        let idx = 0;
        while (respuestasUsuario[`${idPregunta}_blank_${idx}`] !== undefined) {
            respuestasBlanco.push(respuestasUsuario[`${idPregunta}_blank_${idx}`]);
            idx++;
        }

        if (respuestasBlanco.length > 0) {
            // Verificar si es correcta
            const esCorrecta = verificarRespuestaTextoLibre(pregunta, respuestasBlanco);

            return {
                tipo: 'texto_libre',
                contestada: true,
                respuesta: respuestasBlanco.join(' | '), // Concatenar respuestas
                esCorrecta
            };
        }
    }

    // Verificar si es verdadero/falso
    if (pregunta.tipo === 'verdaderoFalso' || (pregunta.idTipoPregunta && pregunta.idTipoPregunta === 3)) {
        const respuestaVF = respuestasUsuario[idPregunta];
        if (respuestaVF !== undefined && respuestaVF !== null) {
            const respuestaCorrecta = pregunta.respuestaVerdaderoFalso?.respuestaCorrecta;
            const esCorrecta = String(respuestaVF) === String(respuestaCorrecta);

            return {
                tipo: 'verdadero_falso',
                contestada: true,
                respuesta: respuestaVF, // boolean string ("true"/"false")
                esCorrecta
            };
        }
    }

    // Verificar si es opción múltiple
    const seleccion = respuestasUsuario[idPregunta];
    if (seleccion) {
        const opciones = pregunta.opciones || [];

        if (Array.isArray(seleccion)) {
            // Múltiples opciones seleccionadas
            const idsSeleccionados = seleccion.map(String);
            const idsCorrectos = opciones
                .filter(o => o.esCorrecta || o.EsCorrecta)
                .map(o => String(o.idOpcion || o.IdOpcion));

            // Calificación estricta: deben coincidir exactamente
            const esCorrecta = idsSeleccionados.length === idsCorrectos.length &&
                idsSeleccionados.every(id => idsCorrectos.includes(id));

            return {
                tipo: 'opcion_multiple_multiple',
                contestada: true,
                idOpciones: idsSeleccionados,
                esCorrecta
            };
        } else {
            // Opción única (simple)
            const idOpcionSeleccionada = seleccion;
            const opcionSeleccionada = opciones.find(
                o => String(o.idOpcion || o.IdOpcion) === String(idOpcionSeleccionada)
            );

            if (opcionSeleccionada) {
                const esCorrecta = opcionSeleccionada.esCorrecta !== undefined
                    ? opcionSeleccionada.esCorrecta
                    : opcionSeleccionada.EsCorrecta !== undefined
                        ? opcionSeleccionada.EsCorrecta
                        : false;

                return {
                    tipo: 'opcion_multiple',
                    contestada: true,
                    idOpcion: idOpcionSeleccionada,
                    esCorrecta
                };
            }
        }
    }

    return {
        tipo: 'desconocido',
        contestada: false
    };
};

/**
 * Verifica si una respuesta de texto libre es correcta
 */
const verificarRespuestaTextoLibre = (pregunta, respuestasUsuario) => {
    console.log('=== VERIFICANDO RESPUESTA TEXTO LIBRE (QUIZ) ===');
    console.log('Pregunta completa:', pregunta);
    console.log('Respuestas del usuario:', respuestasUsuario);

    // Obtener las respuestas correctas
    let respuestasCorrectas = [];

    // Opción 1: Buscar en respuestaTextoLibre
    if (pregunta.respuestaTextoLibre) {
        console.log('Tiene respuestaTextoLibre:', pregunta.respuestaTextoLibre);
        const palabrasClave = pregunta.respuestaTextoLibre.palabrasClave ||
            pregunta.respuestaTextoLibre.PalabrasClave || '';
        if (palabrasClave) {
            respuestasCorrectas = [palabrasClave];
            console.log('Palabras clave encontradas:', palabrasClave);
        }
    }

    // Opción 2: Si no hay respuestas de texto libre, buscar en opciones correctas
    if (respuestasCorrectas.length === 0 && pregunta.opciones) {
        console.log('Buscando en opciones, total opciones:', pregunta.opciones.length);
        const opcionesCorrectas = pregunta.opciones.filter(o => {
            const esCorrecta = o.esCorrecta || o.EsCorrecta || o.correcta;
            console.log(`Opción: "${o.descripcion || o.Descripcion || o.texto || o.Texto}", esCorrecta: ${esCorrecta}`);
            return esCorrecta;
        });

        respuestasCorrectas = opcionesCorrectas.map(o => o.descripcion || o.Descripcion || o.texto || o.Texto || '');
        console.log('Respuestas correctas de opciones:', respuestasCorrectas);
    }

    if (respuestasCorrectas.length === 0) {
        console.warn('⚠️ No se encontraron respuestas correctas definidas para esta pregunta');
        // Por defecto, marcar como correcta si no hay definición
        return true;
    }

    console.log('Respuestas correctas finales:', respuestasCorrectas);
    console.log('Respuestas del usuario a verificar:', respuestasUsuario);

    // Comparar (case-insensitive y trimmed)
    for (let i = 0; i < respuestasUsuario.length; i++) {
        const respuestaUsuario = (respuestasUsuario[i] || '').trim().toLowerCase();
        const respuestaCorrecta = (respuestasCorrectas[i] || respuestasCorrectas[0] || '').trim().toLowerCase();

        console.log(`Comparando [${i}]: "${respuestaUsuario}" === "${respuestaCorrecta}"`);

        if (respuestaUsuario !== respuestaCorrecta) {
            console.log(`❌ NO COINCIDE en posición ${i}`);
            return false;
        } else {
            console.log(`✓ COINCIDE en posición ${i}`);
        }
    }

    console.log('✅ TODAS LAS RESPUESTAS SON CORRECTAS');
    return true;
};

/**
 * Guarda una respuesta individual
 */
const saveIndividualResponse = async ({
    idIntento,
    pregunta,
    tipoRespuesta,
    idPregunta
}) => {
    console.log('=== GUARDANDO RESPUESTA INDIVIDUAL (QUIZ) ===');
    console.log('idIntento:', idIntento);
    console.log('idPregunta:', idPregunta);
    console.log('tipoRespuesta:', tipoRespuesta);

    // Paso 1: Crear el registro en CuestionariosRespuestas
    const respuestaData = {
        IdRespuesta: 0,
        IdIntento: idIntento,
        IdPregunta: idPregunta,
        Correcta: tipoRespuesta.esCorrecta,
        Explicacion: '', // Opcional
        Activo: true
    };

    console.log('📤 POST a CuestionariosRespuestas:', respuestaData);
    console.log('Endpoint:', LMS_ENDPOINTS.cuestionariosRespuestas);

    const resRespuesta = await axios.post(LMS_ENDPOINTS.cuestionariosRespuestas, respuestaData);

    console.log('📥 Respuesta del servidor (CuestionariosRespuestas):', resRespuesta.data);

    const idRespuesta = typeof resRespuesta.data === 'number'
        ? resRespuesta.data
        : resRespuesta.data?.idRespuesta || resRespuesta.data?.IdRespuesta;

    if (!idRespuesta) {
        console.error('❌ No se pudo obtener el ID de la respuesta creada');
        console.error('Respuesta completa del servidor:', resRespuesta);
        throw new Error('No se pudo obtener el ID de la respuesta creada');
    }

    console.log('✅ ID de respuesta obtenido:', idRespuesta);

    // Paso 2: Guardar el detalle según el tipo de respuesta
    if (tipoRespuesta.tipo === 'texto_libre') {
        const textoLibreData = {
            IdRespuestaTextoLibre: 0,
            IdRespuesta: idRespuesta,
            Respuesta: tipoRespuesta.respuesta,
            Activo: true
        };

        console.log('📤 POST a RespuestaCuestionarioTextoLibre:', textoLibreData);
        console.log('Endpoint:', LMS_ENDPOINTS.respuestaCuestionarioTextoLibre);

        const resTextoLibre = await axios.post(LMS_ENDPOINTS.respuestaCuestionarioTextoLibre, textoLibreData);

        console.log('📥 Respuesta del servidor (TextoLibre):', resTextoLibre.data);

    } else if (tipoRespuesta.tipo === 'opcion_multiple') {
        const opcionMultipleData = {
            IdOpcionMultiple_Det: 0,
            IdRespuesta: idRespuesta,
            IdOpcion: parseInt(tipoRespuesta.idOpcion, 10),
            Activo: true
        };

        console.log('📤 POST a RespuestaCuestionarioOpcionMultipleDet:', opcionMultipleData);
        console.log('Endpoint:', LMS_ENDPOINTS.respuestaCuestionarioOpcionMultipleDet);

        const resOpcionMultiple = await axios.post(LMS_ENDPOINTS.respuestaCuestionarioOpcionMultipleDet, opcionMultipleData);

        console.log('📥 Respuesta del servidor (OpcionMultiple):', resOpcionMultiple.data);

    } else if (tipoRespuesta.tipo === 'opcion_multiple_multiple') {
        // Guardar múltiples detalles
        const ids = tipoRespuesta.idOpciones || [];
        console.log(`📤 Guardando ${ids.length} opciones múltiples...`);

        for (const idOpcion of ids) {
            const opcionMultipleData = {
                IdOpcionMultiple_Det: 0,
                IdRespuesta: idRespuesta,
                IdOpcion: parseInt(idOpcion, 10),
                Activo: true
            };
            await axios.post(LMS_ENDPOINTS.respuestaCuestionarioOpcionMultipleDet, opcionMultipleData);
        }
    } else if (tipoRespuesta.tipo === 'verdadero_falso') {
        const verdaderoFalsoData = {
            IdRespuestaVerdaderoFalso: 0,
            IdRespuesta: idRespuesta,
            Respuesta: Boolean(String(tipoRespuesta.respuesta) === 'true'),
            Activo: true
        };

        console.log('📤 POST a RespuestaCuestionarioVerdaderoFalso:', verdaderoFalsoData);
        console.log('Endpoint:', LMS_ENDPOINTS.respuestaCuestionarioVerdaderoFalso);

        const resVerdaderoFalso = await axios.post(LMS_ENDPOINTS.respuestaCuestionarioVerdaderoFalso, verdaderoFalsoData);
        console.log('📥 Respuesta del servidor (VerdaderoFalso):', resVerdaderoFalso.data);
    }

    console.log('✅ Respuesta individual guardada completamente');

    return {
        idRespuesta,
        idPregunta,
        tipo: tipoRespuesta.tipo,
        correcta: tipoRespuesta.esCorrecta
    };
};

/**
 * Obtiene el historial de intentos de un estudiante para un cuestionario
 */
export const getQuizAttempts = async (idCuestionario, idEstudiante) => {
    try {
        const response = await axios.get(LMS_ENDPOINTS.cuestionariosIntentos);
        const todosIntentos = response.data || [];

        const intentosDelEstudiante = todosIntentos.filter(
            intento =>
                (intento.idCuestionario || intento.IdCuestionario) === parseInt(idCuestionario, 10) &&
                (intento.idEstudiante || intento.IdEstudiante) === parseInt(idEstudiante, 10)
        );

        return intentosDelEstudiante.sort((a, b) => {
            const fechaA = new Date(a.fechaIntento || a.FechaIntento);
            const fechaB = new Date(b.fechaIntento || b.FechaIntento);
            return fechaB - fechaA; // Más reciente primero
        });

    } catch (error) {
        console.error('[QuizResponseService] Error obteniendo intentos:', error);
        return [];
    }
};

/**
 * Obtiene las respuestas de un intento específico
 */
export const getAttemptResponses = async (idIntento) => {
    try {
        const [resRespuestas, resTextoLibre, resOpcionMultiple, resVerdaderoFalso] = await Promise.all([
            axios.get(LMS_ENDPOINTS.cuestionariosRespuestas),
            axios.get(LMS_ENDPOINTS.respuestaCuestionarioTextoLibre),
            axios.get(LMS_ENDPOINTS.respuestaCuestionarioOpcionMultipleDet),
            axios.get(LMS_ENDPOINTS.respuestaCuestionarioVerdaderoFalso || '').catch(() => ({ data: [] }))
        ]);

        const todasRespuestas = resRespuestas.data || [];
        const respuestasDelIntento = todasRespuestas.filter(
            r => (r.idIntento || r.IdIntento) === parseInt(idIntento, 10)
        );

        // Enriquecer con detalles
        const respuestasEnriquecidas = respuestasDelIntento.map(respuesta => {
            const idRespuesta = respuesta.idRespuesta || respuesta.IdRespuesta;

            // Buscar detalle de texto libre
            const textoLibre = (resTextoLibre.data || []).find(
                tl => (tl.idRespuesta || tl.IdRespuesta) === idRespuesta
            );

            // Buscar detalle de opción múltiple
            const opcionMultiple = (resOpcionMultiple.data || []).find(
                om => (om.idRespuesta || om.IdRespuesta) === idRespuesta
            );

            // Buscar detalle de verdadero falso
            const verdaderoFalso = (resVerdaderoFalso.data || []).find(
                vf => (vf.idRespuesta || vf.IdRespuesta) === idRespuesta
            );

            return {
                ...respuesta,
                textoLibre,
                opcionMultiple,
                verdaderoFalso
            };
        });

        return respuestasEnriquecidas;

    } catch (error) {
        console.error('[QuizResponseService] Error obteniendo respuestas del intento:', error);
        return [];
    }
};
